/**
 * @NOTE - Types in these files should map to the equivalent types in our `mem-client-desktop` project.
 */

/**
 * Matches a subset of the events defined in the Recall.ai Desktop SDK.
 * - https://docs.recall.ai/docs/desktop-sdk
 *
 * Note that `realtime-event` can match a variety of events which are "registered" by
 * our backend.
 *
 * For more info, see the `recall client` in our python backend code.
 */
export enum RecallEventKind {
  RealtimeEvent = "realtime-event",
  MeetingDetected = "meeting-detected",
  UploadProgress = "upload-progress",
  RecordingEnded = "recording-ended",
}

/**
 * Matches a subset of the events defined in the Recall.ai Desktop SDK.
 * - https://docs.recall.ai/docs/desktop-sdk
 */
export type RecallEventWindow = {
  id: string;
  title?: string;
  url?: string;
  platform: string;
};

/**
 * Matches a subset of the events defined in the Recall.ai Desktop SDK.
 * - https://docs.recall.ai/docs/desktop-sdk
 */
export enum RecallMeetingDetectedEventKind {
  MeetingDetected = "meeting-detected",
  MeetingClosed = "meeting-closed",
}

/**
 * Matches the permission kinds defined in the Recall.ai Desktop SDK.
 * - https://docs.recall.ai/docs/desktop-sdk
 */
export enum RecallPermissionKind {
  Accessibility = "accessibility",
  ScreenCapture = "screen-capture",
  SystemAudio = "system-audio",
  Microphone = "microphone",
  FullDiskAccess = "full-disk-access",
}

/**
 * Matches the statuses defined in the Recall.ai Desktop SDK.
 * - https://docs.recall.ai/docs/desktop-sdk
 */
export enum RecallPermissionStatus {
  Granted = "granted",
  NotRequested = "not_requested",
  Denied = "denied",
}

export enum RecallSdkState {
  Idle = "idle",
  Initializing = "initializing",
  Running = "running",
  Crashed = "crashed",
}

export type RecallPermissionMap = Partial<Record<RecallPermissionKind, RecallPermissionStatus>>;

export interface CalendarEvent {
  id: string;
  calendarEventId: string;
  title?: string;
  url?: string;
  platform: string;
  startTime?: string | null;
  endTime?: string | null;
  attendees?: string[] | null;
}

export type ElectronAppInfo = {
  electronSystemArchitecture: string;
  electronAppVersion: string;
  electronRuntimeVersion: string;
};

/**
 * Result from importing Apple Notes via IPC
 */
export type ImportAppleNotesResult = {
  success: boolean;
  error?: string;
  notes: ImportedAppleNote[];
  notesPerTag: Record<string, number>;
};

/**
 * An imported Apple Note from the IPC channel
 */
export type ImportedAppleNote = {
  id: number;
  title: string;
  folder: string;
  folderPath: string;
  account: string;
  createdAt: string;
  modifiedAt: string;
  isPinned: boolean;
  tags: string[];
  content: string;
};
